import type { SlashCommand, MatchedCommand } from './types';

export interface MatchInput {
  command: SlashCommand;
  localizedTitle: string;
  localizedSubtitle: string;
  /** All searchable strings combined (title, subtitle, keywords, localized keywords, id). */
  haystack: string[];
}

/**
 * Score a single haystack string against the query.
 * Returns a score in [0, 100], or -1 if no match.
 *
 * Heuristic (higher is better):
 *   - Exact equality                  → 100
 *   - Starts with query               →  80 + boost by length ratio
 *   - Contains query as substring     →  50 + boost if at a word boundary
 *   - All query chars appear in order →  20 + small boost for compactness
 *   - Otherwise                       →  -1
 */
function scoreAgainst(haystack: string, query: string): number {
  if (!query) return 0;
  if (!haystack) return -1;

  const h = haystack.toLowerCase();
  const q = query.toLowerCase();

  if (h === q) return 100;
  if (h.startsWith(q)) {
    return 80 + Math.floor((q.length / h.length) * 10);
  }

  const idx = h.indexOf(q);
  if (idx >= 0) {
    const prev = idx === 0 ? ' ' : h[idx - 1];
    const onWordBoundary = prev === ' ' || prev === '.' || prev === '-' || prev === '/' || prev === ':';
    return 50 + (onWordBoundary ? 10 : 0);
  }

  // Subsequence match: all characters of q appear in order in h.
  let hi = 0;
  let firstHit = -1;
  let lastHit = -1;
  for (let qi = 0; qi < q.length; qi++) {
    const ch = q[qi];
    while (hi < h.length && h[hi] !== ch) hi++;
    if (hi >= h.length) return -1;
    if (firstHit === -1) firstHit = hi;
    lastHit = hi;
    hi++;
  }
  // Compactness bonus: shorter span between first and last matched char = better.
  const span = lastHit - firstHit + 1;
  const compactness = span > 0 ? Math.min(10, Math.floor((q.length / span) * 10)) : 0;
  return 20 + compactness;
}

/**
 * Match a single command. Scores the query against the best of all haystack strings
 * (title weighted highest). Returns null if nothing matches.
 */
function scoreCommand(input: MatchInput, query: string): MatchedCommand | null {
  if (!query) {
    return {
      command: input.command,
      score: 0,
      localizedTitle: input.localizedTitle,
      localizedSubtitle: input.localizedSubtitle,
    };
  }

  // Title match is weighted 1.2x, subtitle 1.0x, everything else 0.7x.
  let best = -1;
  for (let i = 0; i < input.haystack.length; i++) {
    const raw = scoreAgainst(input.haystack[i], query);
    if (raw < 0) continue;
    const weight = i === 0 ? 1.2 : i === 1 ? 1.0 : 0.7;
    const weighted = raw * weight;
    if (weighted > best) best = weighted;
  }

  if (best < 0) return null;

  return {
    command: input.command,
    score: best,
    localizedTitle: input.localizedTitle,
    localizedSubtitle: input.localizedSubtitle,
  };
}

/**
 * Filter + score + sort a set of candidate commands against a user query.
 * The query should already be stripped of its leading "/" and trimmed.
 */
export function matchCommands(inputs: MatchInput[], query: string): MatchedCommand[] {
  const results: MatchedCommand[] = [];
  for (const input of inputs) {
    const matched = scoreCommand(input, query);
    if (matched) results.push(matched);
  }
  // Sort by score descending; stable-ish tiebreak on localized title.
  results.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.localizedTitle.localeCompare(b.localizedTitle);
  });
  return results;
}
